import {ComboboxList} from './ComboboxList.tsx'
import {COUNTRIES_NL_SORTED} from '../../types/countries.ts'
import type {Country} from '../../generated/enums.ts'

type CountryOption = {
    value: Country
    displayName: string
    id: string
    searchAliases: string[]
}

const COUNTRY_OPTIONS: CountryOption[] = COUNTRIES_NL_SORTED.map((c) => ({
    value: c.iso,
    displayName: c.nameNl,
    id: c.iso,
    searchAliases: [c.country, c.iso],
}))

export type AddressFieldValue = {
    street_name: string
    house_number: string
    city_name: string
    postal_code: string
    country: string
}

const INPUT_CLASSES =
    'block w-full rounded-md bg-white shadow-xs text-gray-900 shadow-sm ring-1 ring-inset ' +
    'ring-gray-300 outline-none py-1.5 px-3 text-base placeholder:text-gray-400 sm:text-sm/6'

type AddressFieldsProps = {
    value: AddressFieldValue
    onChange: (value: AddressFieldValue) => void
    idPrefix?: string
}

export function AddressFields({value, onChange, idPrefix = 'address'}: AddressFieldsProps) {
    function update(patch: Partial<AddressFieldValue>) {
        onChange({...value, ...patch})
    }

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
            <div className="sm:col-span-4">
                <label className="block text-sm text-gray-600 mb-1" htmlFor={`${idPrefix}-street`}>
                    Straatnaam
                </label>
                <input
                    id={`${idPrefix}-street`}
                    className={INPUT_CLASSES}
                    value={value.street_name}
                    onChange={(e) => update({street_name: e.target.value})}
                    placeholder="Damrak"
                />
            </div>
            <div className="sm:col-span-2">
                <label className="block text-sm text-gray-600 mb-1" htmlFor={`${idPrefix}-house-number`}>
                    Huisnummer
                </label>
                <input
                    id={`${idPrefix}-house-number`}
                    className={INPUT_CLASSES}
                    value={value.house_number}
                    onChange={(e) => update({house_number: e.target.value})}
                    placeholder="1"
                />
            </div>
            <div className="sm:col-span-2">
                <label className="block text-sm text-gray-600 mb-1" htmlFor={`${idPrefix}-postal-code`}>
                    Postcode
                </label>
                <input
                    id={`${idPrefix}-postal-code`}
                    className={INPUT_CLASSES}
                    value={value.postal_code}
                    onChange={(e) => update({postal_code: e.target.value})}
                    placeholder="1012LG"
                />
            </div>
            <div className="sm:col-span-4">
                <label className="block text-sm text-gray-600 mb-1" htmlFor={`${idPrefix}-city`}>
                    Plaats
                </label>
                <input
                    id={`${idPrefix}-city`}
                    className={INPUT_CLASSES}
                    value={value.city_name}
                    onChange={(e) => update({city_name: e.target.value})}
                    placeholder="Amsterdam"
                />
            </div>
            <div className="sm:col-span-6">
                <label className="block text-sm text-gray-600 mb-1">
                    Land <span className="text-red-500 ml-1">*</span>
                </label>
                <ComboboxList<CountryOption>
                    anchor={{to: 'bottom start', gap: 4}}
                    menuWidth="w-[var(--input-width)]"
                    openMenuOnFocus={true}
                    comboBoxClassName="w-full min-w-0"
                    menuClassName="py-2"
                    className={`block w-full rounded-md bg-white shadow-xs text-gray-900 shadow-sm ring-1 ring-inset outline-none py-1.5 px-3 text-base placeholder:text-gray-400 sm:text-sm/6 ${
                        value.country.trim() ? 'ring-gray-300' : 'ring-red-500'
                    }`}
                    emptySelectionMessage="Selecteer land..."
                    options={COUNTRY_OPTIONS}
                    selected={COUNTRY_OPTIONS.find((opt) => opt.value === value.country) ?? null}
                    onSelect={(option) => update({country: option ? option.value : ''})}
                    internalMapper={(item) => item}
                    showClearButton={false}
                />
            </div>
        </div>
    )
}
